import { Injectable, NgZone } from '@angular/core';
import {
  HttpEvent,
  HttpHandler,
  HttpInterceptor,
  HttpRequest,
  HttpResponse
} from '@angular/common/http';
import {from, Observable, of, throwError} from 'rxjs';
import {catchError, shareReplay, switchMap, take, tap, timeout} from 'rxjs/operators';
import { CHANGE_PASS_CAPTCHA_PATH, CAPTCHA_STATUS_API, RECAPTCHA_API_LIST, setRecaptchaApiList } from '../config/recaptcha-api.config';
import {CapService} from "../service/cap.service";
import {ApiService} from "../custom-services/ApiService";
import {environment} from "../../environments/environment";

@Injectable()
export class RecaptchaInterceptor implements HttpInterceptor {

  private captchaStatus$: Observable<any> = null;

  constructor(
              private capService: CapService,
              private apiService: ApiService,
              private ngZone: NgZone,
  ) {}

  normalizeCaptchaStatus(result: any): any {
    const source = result && result.replyMessage ? result.replyMessage : result;
    return source || { enabled: false, paths: [] };
  }

  isCaptchaRequiredUrl(url: string): boolean {
    if (!url) {
      return false;
    }
    if (url.indexOf(CHANGE_PASS_CAPTCHA_PATH) >= 0) {
      return true;
    }
    for (let i = 0; i < RECAPTCHA_API_LIST.length; i++) {
      const api = RECAPTCHA_API_LIST[i];
      if (api && url.indexOf(api) >= 0) {
        return true;
      }
    }
    return false;
  }

  needsBodyCaptcha(req: HttpRequest<any>): boolean {
    if (!req || !req.url || req.url.indexOf('/filingservice/') < 0) {
      return false;
    }
    const body = req.body;
    if (!body) {
      return false;
    }
    if (typeof body === 'string') {
      const trimmed = body.replace(/^\s+/, '');
      return trimmed.charAt(0) === '{';
    }
    return typeof body === 'object' && !Array.isArray(body);
  }

  attachCaptcha(req: HttpRequest<any>, headerToken: string, bodyToken: string): HttpRequest<any> {
    let body = req.body;
    if (typeof body === 'string' && body) {
      try {
        const parsed = JSON.parse(body);
        if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
          if (bodyToken) {
            parsed.captcha = bodyToken;
          } else {
            delete parsed.captcha;
          }
          body = JSON.stringify(parsed);
        }
      } catch (e) {
      }
    } else if (body && typeof body === 'object' && !Array.isArray(body)) {
      const copy: any = {};
      for (const key in body) {
        if (body.hasOwnProperty(key) && key !== 'captcha') {
          copy[key] = body[key];
        }
      }
      if (bodyToken) {
        copy.captcha = bodyToken;
      }
      body = copy;
    }
    return req.clone({
      body: body,
      setHeaders: {
        'Content-Type': 'application/json',
        'Captcha': headerToken
      }
    });
  }

  isInvalidCaptcha(error: any): boolean {
    const body = error && error.error ? error.error : error;
    const code = body && body.errorCode ? String(body.errorCode) : '';
    return code === 'INVALID_CAPTCHA';
  }

  sendWithCaptcha(req: HttpRequest<any>, next: HttpHandler, status: any): Observable<HttpEvent<any>> {
    return from(this.capService.getToken(status.siteKey, status.baseUrl)).pipe(
      switchMap(headerToken => {
        if (!headerToken) {
          throw new Error('Failed to generate captcha token');
        }
        if (!this.needsBodyCaptcha(req)) {
          return this.handleInZone(next, this.attachCaptcha(req, headerToken, ''));
        }
        return from(this.capService.getToken(status.siteKey, status.baseUrl)).pipe(
          switchMap(bodyToken => {
            if (!bodyToken) {
              throw new Error('Failed to generate captcha token');
            }
            return this.handleInZone(next, this.attachCaptcha(req, headerToken, bodyToken));
          })
        );
      })
    );
  }

  handleInZone(next: HttpHandler, req: HttpRequest<any>): Observable<HttpEvent<any>> {
    return new Observable(observer => {
      return this.ngZone.run(() => {
        return next.handle(req).subscribe(observer);
      });
    });
  }

  endWhenHttpResponse(source: Observable<HttpEvent<any>>): Observable<HttpEvent<any>> {
    return new Observable(observer => {
      const sub = source.subscribe(
        (event: HttpEvent<any>) => {
          observer.next(event);
          if (event instanceof HttpResponse) {
            observer.complete();
          }
        },
        (err: any) => {
          observer.error(err);
        },
        () => {
          observer.complete();
        }
      );
      return () => {
        sub.unsubscribe();
      };
    });
  }

  dispatchRequest(req: HttpRequest<any>, next: HttpHandler, forceRefresh: boolean): Observable<HttpEvent<any>> {
    return this.getCaptchaStatus(forceRefresh).pipe(
      take(1),
      switchMap((result: any) => {
        const status = this.normalizeCaptchaStatus(result);
        const isRecaptchaRequired = this.isCaptchaRequiredUrl(req.url);

        if (!isRecaptchaRequired || !status.enabled) {
          return next.handle(req);
        }

        return this.sendWithCaptcha(req, next, status);
      })
    );
  }

  getCaptchaStatus(forceRefresh?: boolean): Observable<any> {
    if (forceRefresh) {
      this.captchaStatus$ = null;
    }
    if (!this.captchaStatus$) {
      this.captchaStatus$ = this.apiService
        .get(CAPTCHA_STATUS_API)
        .pipe(
          timeout(4000),
          tap((result: any) => {
            const status = this.normalizeCaptchaStatus(result);
            setRecaptchaApiList(status && status.paths);
          }),
          catchError(() => {
            setRecaptchaApiList([]);
            return of({enabled: false, paths: []});
          }),
          shareReplay(1)
        );
    }
    return this.captchaStatus$;
  }

  intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {

    if (req.url.includes('/api/captcha/get-status')) {
      return next.handle(req);
    }

    const method = (req.method || 'GET').toUpperCase();
    if (method === 'GET' || method === 'HEAD' || method === 'OPTIONS') {
      return next.handle(req);
    }

    return this.endWhenHttpResponse(
      this.dispatchRequest(req, next, false).pipe(
        catchError(error => {
          if (!this.isInvalidCaptcha(error)) {
            return throwError(error);
          }
          return this.dispatchRequest(req, next, true);
        })
      )
    );
  }

}
